import { useMemo } from 'react';
import { Box, Text } from '@chakra-ui/react';

import {
  QuoteCarrierHeader,
  QuoteBanner,
  MessagesSection,
  PremiumSummarySection,
  QuoteActionButtons,
} from '../components/quote';
import LocationSummarySection from '../components/quote/LocationSummarySection';
import LocationQuoteCard from '../components/quote/LocationQuoteCard';
import PolicyCoverageSection from '../components/quote/PolicyCoverageSection';
import type { PropertyQuoteSummaryData } from 'api/quote-marketplace';
import type { QuoteLOB } from '../constants';
import { getLocationsParsedData } from '../utils/parseLocationHtml';

interface PropertyQuoteStepProps {
  data: PropertyQuoteSummaryData;
  quoteId: number;
  lob: QuoteLOB;
}

const PropertyQuoteStep = ({ data, quoteId, lob }: PropertyQuoteStepProps) => {
  const { selected_carrier, locations } = data;

  const locationsWithParsedData = useMemo(() => {
    const parsedData = getLocationsParsedData(
      locations,
      selected_carrier?.coverage_breakdown_html ?? null,
      selected_carrier?.discounts_html ?? null,
    );
    return locations.map((location, index) => ({
      location,
      parsedData: parsedData[index],
    }));
  }, [
    locations,
    selected_carrier?.coverage_breakdown_html,
    selected_carrier?.discounts_html,
  ]);

  if (!selected_carrier) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="gray.500">No carrier selected</Text>
      </Box>
    );
  }

  return (
    <Box>
      <QuoteCarrierHeader
        carrierName={selected_carrier.carrier_name}
        quoteRef={selected_carrier.acturis_quote_ref}
      />

      <QuoteBanner title="Home">
        <LocationSummarySection
          locations={locations}
          annualPremium={selected_carrier.annual_premium}
          policyPremium={selected_carrier.policy_premium}
          taxes={selected_carrier.taxes}
        />

        <PolicyCoverageSection html={selected_carrier.policy_coverage_html} />

        {locationsWithParsedData.map(({ location, parsedData }, index) => (
          <LocationQuoteCard
            key={location.id}
            location={location}
            parsedData={parsedData}
            locationIndex={index}
          />
        ))}

        <MessagesSection ratingNotes={selected_carrier.rating_notes} />

        <PremiumSummarySection
          annualPremium={selected_carrier.annual_premium}
          taxes={selected_carrier.taxes}
          productLabel="Home"
        />
      </QuoteBanner>

      <QuoteActionButtons quoteId={quoteId} lob={lob} />
    </Box>
  );
};

export default PropertyQuoteStep;
